"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, type IChartApi, type ISeriesApi, type CandlestickData, type LineData, type HistogramData, type SeriesType, type Time, ColorType, CrosshairMode } from "lightweight-charts";
import { useBinanceKline, useBinanceTicker, type ChartInterval, type KlineData } from "@/hooks/useBinanceWebSocket";
import { sma, ema, rsi, macd, bollingerBands, type OHLCV } from "@/lib/indicators";
import { useBinanceOrderBook } from "@/hooks/useBinanceOrderBook";
import { forecastPrice, type LiquidityLevel } from "@/lib/forecast";
import { LineStyle } from "lightweight-charts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PriceChartProps {
  symbol: string;
  tokenName: string;
  initialPrice?: number | null;
  /** Historical price data from DB (used as fallback when Binance unavailable) */
  historicalPrices?: {
    timestamp: string | number;
    price: number;
  }[];
  /** Historical market cap data from DB */
  marketCapHistory?: {
    timestamp: string | number;
    marketCap: number;
  }[];
  /** Circulating supply — used to compute live market cap */
  circulatingSupply?: number | null;
  /** Current market cap from DB */
  currentMarketCap?: number | null;
}
type ChartType = "candle" | "line";
type DataMode = "price" | "marketcap";
type TradingPair = "USDT" | "BTC" | "ETH";
interface IndicatorState {
  ma20: boolean;
  ma50: boolean;
  ma200: boolean;
  ema21: boolean;
  bbands: boolean;
  rsi: boolean;
  macd: boolean;
  liquidity: boolean;
  forecast: boolean;
}
const DEFAULT_INDICATORS: IndicatorState = {
  ma20: false,
  ma50: false,
  ma200: false,
  ema21: false,
  bbands: false,
  rsi: false,
  macd: false,
  liquidity: false,
  forecast: false
};
const INTERVAL_OPTIONS: {
  label: string;
  value: ChartInterval;
}[] = [{
  label: "1m",
  value: "1m"
}, {
  label: "5m",
  value: "5m"
}, {
  label: "15m",
  value: "15m"
}, {
  label: "1H",
  value: "1h"
}, {
  label: "4H",
  value: "4h"
}, {
  label: "1D",
  value: "1d"
}, {
  label: "1W",
  value: "1w"
}, {
  label: "1M",
  value: "1M"
}];

// ---------------------------------------------------------------------------
// Chart colours (TradingView dark theme style)
// ---------------------------------------------------------------------------

const CHART_COLORS = {
  background: "hsl(230, 15%, 7%)",
  text: "#9ca3af",
  grid: "rgba(255, 255, 255, 0.04)",
  crosshair: "rgba(255, 255, 255, 0.15)",
  upColor: "#22c55e",
  downColor: "#ef4444",
  lineColor: "#3b82f6",
  volumeUp: "rgba(34, 197, 94, 0.25)",
  volumeDown: "rgba(239, 68, 68, 0.25)"
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(price: number): string {
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function formatMarketCap(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return `$${value.toFixed(2)}`;
}
function formatPairPrice(price: number): string {
  if (price < 0.00000001) return price.toExponential(4);
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  return price.toFixed(2);
}
function formatVolume(vol: number): string {
  if (vol >= 1e9) return `${(vol / 1e9).toFixed(2)}B`;
  if (vol >= 1e6) return `${(vol / 1e6).toFixed(2)}M`;
  if (vol >= 1e3) return `${(vol / 1e3).toFixed(2)}K`;
  return vol.toFixed(2);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PriceChart({
  symbol,
  tokenName,
  initialPrice,
  historicalPrices,
  marketCapHistory,
  circulatingSupply,
  currentMarketCap
}: PriceChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<SeriesType> | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<SeriesType> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<SeriesType> | null>(null);
  const indicatorSeriesRefs = useRef<ISeriesApi<SeriesType>[]>([]);
  const liquidityPriceLinesRef = useRef<{
    series: ISeriesApi<SeriesType>;
    line: ReturnType<ISeriesApi<SeriesType>["createPriceLine"]>;
  }[]>([]);
  const [forecastSummary, setForecastSummary] = useState<{
    trend: "bullish" | "bearish" | "neutral";
    targetPrice: number;
    targetChangePct: number;
    confidencePct: number;
  } | null>(null);
  const [interval, setInterval] = useState<ChartInterval>("1h");
  const [chartType, setChartType] = useState<ChartType>("candle");
  const [dataMode, setDataMode] = useState<DataMode>("price");
  const [tradingPair, setTradingPair] = useState<TradingPair>("USDT");
  const [hoveredPrice, setHoveredPrice] = useState<number | null>(null);
  const [hoveredTime, setHoveredTime] = useState<string | null>(null);
  const [indicators, setIndicators] = useState<IndicatorState>(DEFAULT_INDICATORS);
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const toggleIndicator = (key: keyof IndicatorState) => setIndicators(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  const {
    klines,
    currentKline,
    isConnected,
    error
  } = useBinanceKline(symbol, interval);
  const {
    ticker
  } = useBinanceTicker(symbol);
  // Liquidity data — only fetched when overlay is enabled (refreshMs=0 would be invalid;
  // we skip effect work by checking the toggle in the render pass instead)
  const {
    snapshot: orderBookSnapshot
  } = useBinanceOrderBook(symbol, 1000, 20000);

  // Get BTC and ETH prices for pair conversion
  const {
    ticker: btcTicker
  } = useBinanceTicker("BTC");
  const {
    ticker: ethTicker
  } = useBinanceTicker("ETH");
  const livePairPrice = (tradingPair === "BTC" ? btcTicker?.price : tradingPair === "ETH" ? ethTicker?.price : null) ?? null;

  // Snapshot the pair price when tradingPair changes so we don't rebuild chart on every tick
  const [snappedPairPrice, setSnappedPairPrice] = useState<number | null>(null);
  const pairPriceRef = useRef<number | null>(null);
  pairPriceRef.current = livePairPrice;
  useEffect(() => {
    if (tradingPair === "USDT") {
      setSnappedPairPrice(null);
      return;
    }
    // If price is already available, use it immediately
    if (pairPriceRef.current) {
      setSnappedPairPrice(pairPriceRef.current);
      return;
    }
    // Otherwise poll briefly until available
    const id = window.setInterval(() => {
      if (pairPriceRef.current) {
        setSnappedPairPrice(pairPriceRef.current);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [tradingPair]);
  const isBinanceAvailable = error !== "not_available";

  // Use DB historical data as fallback
  const fallbackData = useMemo(() => {
    if (isBinanceAvailable || !historicalPrices?.length) return null;
    return historicalPrices.map(p => ({
      time: (typeof p.timestamp === "number" ? Math.floor(p.timestamp / 1000) : Math.floor(new Date(p.timestamp).getTime() / 1000)) as Time,
      value: p.price
    })).sort((a, b) => (a.time as number) - (b.time as number));
  }, [isBinanceAvailable, historicalPrices]);

  // Market cap data — from DB market cap history, or computed from price * supply
  const marketCapData = useMemo(() => {
    // First try DB market cap history
    if (marketCapHistory?.length) {
      return marketCapHistory.map(p_0 => ({
        time: (typeof p_0.timestamp === "number" ? Math.floor(p_0.timestamp / 1000) : Math.floor(new Date(p_0.timestamp).getTime() / 1000)) as Time,
        value: p_0.marketCap
      })).sort((a_0, b_0) => (a_0.time as number) - (b_0.time as number));
    }
    // Compute from price history * circulating supply
    if (historicalPrices?.length && circulatingSupply && circulatingSupply > 0) {
      return historicalPrices.map(p_1 => ({
        time: (typeof p_1.timestamp === "number" ? Math.floor(p_1.timestamp / 1000) : Math.floor(new Date(p_1.timestamp).getTime() / 1000)) as Time,
        value: p_1.price * circulatingSupply
      })).sort((a_1, b_1) => (a_1.time as number) - (b_1.time as number));
    }
    return null;
  }, [marketCapHistory, historicalPrices, circulatingSupply]);

  // Current display price — convert for trading pair
  const rawPrice = hoveredPrice ?? ticker?.price ?? currentKline?.close ?? initialPrice;
  const activePairPrice = livePairPrice ?? snappedPairPrice;
  const displayPrice = dataMode === "marketcap" ? hoveredPrice ?? (rawPrice && circulatingSupply ? rawPrice * circulatingSupply : currentMarketCap) : tradingPair !== "USDT" && rawPrice && activePairPrice ? rawPrice / activePairPrice : rawPrice;
  const priceChangePercent = ticker?.priceChangePercent ?? null;
  const pairLabel = tradingPair === "USDT" ? "USD" : tradingPair;

  // Create chart
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: {
          type: ColorType.Solid,
          color: CHART_COLORS.background
        },
        textColor: CHART_COLORS.text,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12
      },
      grid: {
        vertLines: {
          color: CHART_COLORS.grid
        },
        horzLines: {
          color: CHART_COLORS.grid
        }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: CHART_COLORS.crosshair,
          labelBackgroundColor: "#374151"
        },
        horzLine: {
          color: CHART_COLORS.crosshair,
          labelBackgroundColor: "#374151"
        }
      },
      rightPriceScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        scaleMargins: {
          top: 0.1,
          bottom: 0.2
        }
      },
      timeScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        timeVisible: true,
        secondsVisible: false
      },
      handleScroll: {
        vertTouchDrag: false
      }
    });
    chartRef.current = chart;

    // Subscribe to crosshair move for tooltip
    chart.subscribeCrosshairMove(param => {
      if (!param.time || !param.seriesData.size) {
        setHoveredPrice(null);
        setHoveredTime(null);
        return;
      }

      // Get price from the first series
      const firstSeries = param.seriesData.entries().next().value;
      if (firstSeries) {
        const data = firstSeries[1] as {
          close?: number;
          value?: number;
        } | undefined;
        if (data) {
          setHoveredPrice(data.close ?? data.value ?? null);
        }
      }

      // Format time
      if (typeof param.time === "number") {
        const d = new Date(param.time * 1000);
        setHoveredTime(d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        }));
      }
    });

    // Resize observer
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        chart.applyOptions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    resizeObserver.observe(chartContainerRef.current);
    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      lineSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  // Update series when chart type or data changes
  useEffect(() => {
    const chart_0 = chartRef.current;
    if (!chart_0) return;

    // Remove existing series
    if (candleSeriesRef.current) {
      chart_0.removeSeries(candleSeriesRef.current);
      candleSeriesRef.current = null;
    }
    if (lineSeriesRef.current) {
      chart_0.removeSeries(lineSeriesRef.current);
      lineSeriesRef.current = null;
    }
    if (volumeSeriesRef.current) {
      chart_0.removeSeries(volumeSeriesRef.current);
      volumeSeriesRef.current = null;
    }
    for (const s of indicatorSeriesRefs.current) {
      try {
        chart_0.removeSeries(s);
      } catch {
        // ignore
      }
    }
    indicatorSeriesRefs.current = [];

    // Helper to convert price for trading pair
    const convertPrice = (p_2: number) => {
      if (tradingPair !== "USDT" && snappedPairPrice && snappedPairPrice > 0) return p_2 / snappedPairPrice;
      return p_2;
    };

    // Market cap mode — line chart from DB data or computed
    if (dataMode === "marketcap" && marketCapData) {
      const mcLineSeries = chart_0.addSeries(LineSeries, {
        color: "#8b5cf6",
        lineWidth: 2,
        priceLineVisible: true,
        lastValueVisible: true,
        priceFormat: {
          type: "custom",
          formatter: (price: number) => {
            if (price >= 1e12) return `$${(price / 1e12).toFixed(2)}T`;
            if (price >= 1e9) return `$${(price / 1e9).toFixed(2)}B`;
            if (price >= 1e6) return `$${(price / 1e6).toFixed(2)}M`;
            return `$${price.toLocaleString()}`;
          }
        }
      });
      mcLineSeries.setData(marketCapData as LineData<Time>[]);
      lineSeriesRef.current = mcLineSeries;
      chart_0.timeScale().fitContent();
      return;
    }

    // Fallback mode — line chart from DB data
    if (!isBinanceAvailable && fallbackData) {
      const lineSeries = chart_0.addSeries(LineSeries, {
        color: CHART_COLORS.lineColor,
        lineWidth: 2,
        priceLineVisible: true,
        lastValueVisible: true
      });
      lineSeries.setData(fallbackData as LineData<Time>[]);
      lineSeriesRef.current = lineSeries;
      chart_0.timeScale().fitContent();
      return;
    }
    if (klines.length === 0) return;

    // Price format for non-USDT pairs (more decimals)
    const pairPriceFormat = tradingPair !== "USDT" ? {
      type: "price" as const,
      precision: 8,
      minMove: 0.00000001
    } : undefined;
    if (chartType === "candle") {
      // Candlestick series
      const candleSeries = chart_0.addSeries(CandlestickSeries, {
        upColor: CHART_COLORS.upColor,
        downColor: CHART_COLORS.downColor,
        borderUpColor: CHART_COLORS.upColor,
        borderDownColor: CHART_COLORS.downColor,
        wickUpColor: CHART_COLORS.upColor,
        wickDownColor: CHART_COLORS.downColor,
        ...(pairPriceFormat && {
          priceFormat: pairPriceFormat
        })
      });
      const candleData: CandlestickData<Time>[] = klines.map(k => ({
        time: k.time as Time,
        open: convertPrice(k.open),
        high: convertPrice(k.high),
        low: convertPrice(k.low),
        close: convertPrice(k.close)
      }));
      candleSeries.setData(candleData);
      candleSeriesRef.current = candleSeries;
    } else {
      // Line series
      const lineSeries_0 = chart_0.addSeries(LineSeries, {
        color: CHART_COLORS.lineColor,
        lineWidth: 2,
        priceLineVisible: true,
        lastValueVisible: true,
        ...(pairPriceFormat && {
          priceFormat: pairPriceFormat
        })
      });
      const lineData: LineData<Time>[] = klines.map(k_0 => ({
        time: k_0.time as Time,
        value: convertPrice(k_0.close)
      }));
      lineSeries_0.setData(lineData);
      lineSeriesRef.current = lineSeries_0;
    }

    // Volume histogram
    const volumeSeries = chart_0.addSeries(HistogramSeries, {
      priceFormat: {
        type: "volume"
      },
      priceScaleId: "volume"
    });
    chart_0.priceScale("volume").applyOptions({
      scaleMargins: {
        top: 0.85,
        bottom: 0
      }
    });
    const volumeData = klines.map(k_1 => ({
      time: k_1.time as Time,
      value: k_1.volume,
      color: k_1.close >= k_1.open ? CHART_COLORS.volumeUp : CHART_COLORS.volumeDown
    }));
    volumeSeries.setData(volumeData);
    volumeSeriesRef.current = volumeSeries;

    // --- Technical Indicators ---
    if (dataMode === "price" && klines.length > 0) {
      const ohlcv: OHLCV[] = klines.map(k_2 => ({
        time: k_2.time,
        open: convertPrice(k_2.open),
        high: convertPrice(k_2.high),
        low: convertPrice(k_2.low),
        close: convertPrice(k_2.close),
        volume: k_2.volume
      }));
      const addLine = (points: {
        time: number;
        value: number;
      }[], color: string, title: string) => {
        if (points.length === 0) return;
        const series = chart_0.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          lastValueVisible: false,
          priceLineVisible: false,
          title,
          ...(pairPriceFormat && {
            priceFormat: pairPriceFormat
          })
        });
        series.setData(points.map(p_3 => ({
          time: p_3.time as Time,
          value: p_3.value
        })) as LineData<Time>[]);
        indicatorSeriesRefs.current.push(series);
      };
      if (indicators.ma20) addLine(sma(ohlcv, 20), "#f59e0b", "MA 20");
      if (indicators.ma50) addLine(sma(ohlcv, 50), "#3b82f6", "MA 50");
      if (indicators.ma200) addLine(sma(ohlcv, 200), "#ec4899", "MA 200");
      if (indicators.ema21) addLine(ema(ohlcv, 21), "#a855f7", "EMA 21");
      if (indicators.bbands) {
        const bb = bollingerBands(ohlcv, 20, 2);
        addLine(bb.map(p_4 => ({
          time: p_4.time,
          value: p_4.upper
        })), "rgba(139, 92, 246, 0.7)", "BB Upper");
        addLine(bb.map(p_5 => ({
          time: p_5.time,
          value: p_5.middle
        })), "rgba(139, 92, 246, 0.4)", "BB Mid");
        addLine(bb.map(p_6 => ({
          time: p_6.time,
          value: p_6.lower
        })), "rgba(139, 92, 246, 0.7)", "BB Lower");
      }

      // RSI in a separate pane (pane index 1)
      if (indicators.rsi) {
        const rsiData = rsi(ohlcv, 14);
        if (rsiData.length > 0) {
          const rsiSeries = chart_0.addSeries(LineSeries, {
            color: "#06b6d4",
            lineWidth: 2,
            title: "RSI 14",
            priceFormat: {
              type: "price",
              precision: 2,
              minMove: 0.01
            },
            priceLineVisible: false,
            lastValueVisible: true
          }, 1 // pane index
          );
          rsiSeries.setData(rsiData.map(p_7 => ({
            time: p_7.time as Time,
            value: p_7.value
          })) as LineData<Time>[]);
          // Add 70/30 reference lines
          rsiSeries.createPriceLine({
            price: 70,
            color: "rgba(239, 68, 68, 0.5)",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "Overbought"
          });
          rsiSeries.createPriceLine({
            price: 30,
            color: "rgba(34, 197, 94, 0.5)",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "Oversold"
          });
          indicatorSeriesRefs.current.push(rsiSeries);
        }
      }

      // MACD in a separate pane (pane index 2 if rsi is on, else 1)
      if (indicators.macd) {
        const macdData = macd(ohlcv);
        if (macdData.length > 0) {
          const macdPane = indicators.rsi ? 2 : 1;
          const macdLine = chart_0.addSeries(LineSeries, {
            color: "#3b82f6",
            lineWidth: 2,
            title: "MACD",
            priceLineVisible: false,
            lastValueVisible: true
          }, macdPane);
          macdLine.setData(macdData.map(p_8 => ({
            time: p_8.time as Time,
            value: p_8.macd
          })) as LineData<Time>[]);
          const signalLine = chart_0.addSeries(LineSeries, {
            color: "#f59e0b",
            lineWidth: 2,
            title: "Signal",
            priceLineVisible: false,
            lastValueVisible: true
          }, macdPane);
          signalLine.setData(macdData.map(p_9 => ({
            time: p_9.time as Time,
            value: p_9.signal
          })) as LineData<Time>[]);
          const histSeries = chart_0.addSeries(HistogramSeries, {
            priceFormat: {
              type: "price",
              precision: 4,
              minMove: 0.0001
            },
            priceLineVisible: false,
            lastValueVisible: false
          }, macdPane);
          histSeries.setData(macdData.map(p_10 => ({
            time: p_10.time as Time,
            value: p_10.histogram,
            color: p_10.histogram >= 0 ? "rgba(34, 197, 94, 0.6)" : "rgba(239, 68, 68, 0.6)"
          })) as HistogramData<Time>[]);
          indicatorSeriesRefs.current.push(macdLine, signalLine, histSeries);
        }
      }

      // --- Price Forecast ---
      if (indicators.forecast && ohlcv.length >= 65) {
        // Extract liquidity levels from order book for gravity effect
        let liquidityLevels: LiquidityLevel[] | undefined;
        if (orderBookSnapshot && tradingPair === "USDT" && orderBookSnapshot.midPrice) {
          liquidityLevels = [...orderBookSnapshot.bids.slice(0, 100).map(b_2 => ({
            price: b_2.price,
            notional: b_2.notional,
            side: "bid" as const
          })), ...orderBookSnapshot.asks.slice(0, 100).map(a_2 => ({
            price: a_2.price,
            notional: a_2.notional,
            side: "ask" as const
          }))];
        }

        // Horizon = 20% of visible history, min 10 max 50 bars
        const horizon = Math.max(10, Math.min(50, Math.round(ohlcv.length * 0.2)));
        const forecast = forecastPrice(ohlcv, {
          horizon,
          lookback: Math.min(60, ohlcv.length - 5),
          confidenceSigma: 1.645,
          // 90% CI
          liquidity: liquidityLevels
        });
        if (forecast && forecast.points.length > 0) {
          // Prepend the current bar so forecast lines connect to live price
          const connector = {
            time: ohlcv[ohlcv.length - 1]!.time as Time,
            center: ohlcv[ohlcv.length - 1]!.close,
            upper: ohlcv[ohlcv.length - 1]!.close,
            lower: ohlcv[ohlcv.length - 1]!.close
          };
          const centerData: LineData<Time>[] = [{
            time: connector.time,
            value: connector.center
          }, ...forecast.points.map(p_11 => ({
            time: p_11.time as Time,
            value: p_11.price
          }))];
          const upperData: LineData<Time>[] = [{
            time: connector.time,
            value: connector.upper
          }, ...forecast.points.map(p_12 => ({
            time: p_12.time as Time,
            value: p_12.upper
          }))];
          const lowerData: LineData<Time>[] = [{
            time: connector.time,
            value: connector.lower
          }, ...forecast.points.map(p_13 => ({
            time: p_13.time as Time,
            value: p_13.lower
          }))];
          const forecastColor = forecast.summary.trend === "bullish" ? "#22c55e" : forecast.summary.trend === "bearish" ? "#ef4444" : "#eab308";

          // Upper bound (dashed, semi-transparent)
          const upperSeries = chart_0.addSeries(LineSeries, {
            color: `${forecastColor}66`,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            lastValueVisible: false,
            priceLineVisible: false,
            title: "Forecast Upper",
            ...(pairPriceFormat && {
              priceFormat: pairPriceFormat
            })
          });
          upperSeries.setData(upperData);

          // Lower bound
          const lowerSeries = chart_0.addSeries(LineSeries, {
            color: `${forecastColor}66`,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            lastValueVisible: false,
            priceLineVisible: false,
            title: "Forecast Lower",
            ...(pairPriceFormat && {
              priceFormat: pairPriceFormat
            })
          });
          lowerSeries.setData(lowerData);

          // Center forecast line (bolder, dashed)
          const centerSeries = chart_0.addSeries(LineSeries, {
            color: forecastColor,
            lineWidth: 2,
            lineStyle: LineStyle.LargeDashed,
            lastValueVisible: true,
            priceLineVisible: false,
            title: `Forecast (${forecast.summary.trend})`,
            ...(pairPriceFormat && {
              priceFormat: pairPriceFormat
            })
          });
          centerSeries.setData(centerData);
          indicatorSeriesRefs.current.push(upperSeries, lowerSeries, centerSeries);
          setForecastSummary({
            trend: forecast.summary.trend,
            targetPrice: forecast.summary.targetPrice,
            targetChangePct: forecast.summary.targetChangePct,
            confidencePct: forecast.summary.confidencePct
          });
        } else {
          setForecastSummary(null);
        }
      } else if (!indicators.forecast) {
        setForecastSummary(null);
      }
    }
    chart_0.timeScale().fitContent();
  }, [klines, chartType, dataMode, tradingPair, snappedPairPrice, isBinanceAvailable, fallbackData, marketCapData, indicators, orderBookSnapshot]);

  // --- Liquidity overlay: draw top bid/ask walls as price lines on main series ---
  useEffect(() => {
    // Clear existing liquidity lines
    for (const {
      series: series_0,
      line
    } of liquidityPriceLinesRef.current) {
      try {
        series_0.removePriceLine(line);
      } catch {
        // series may have been removed — ignore
      }
    }
    liquidityPriceLinesRef.current = [];
    if (!indicators.liquidity || !orderBookSnapshot || dataMode !== "price" || tradingPair !== "USDT" // order book is USDT-denominated
    ) {
      return;
    }
    const mainSeries = candleSeriesRef.current ?? lineSeriesRef.current;
    if (!mainSeries) return;

    // Aggregate depth into buckets (±5% of mid) so scattered orders form visible walls
    const mid = orderBookSnapshot.midPrice;
    if (!mid) return;
    const minPrice = mid * 0.95;
    const maxPrice = mid * 1.05;
    const BUCKET_COUNT = 50;
    const bucketSize = (maxPrice - minPrice) / BUCKET_COUNT;
    interface LiquidityBucket {
      price: number;
      notional: number;
      side: "bid" | "ask";
    }
    const bidBuckets = new Map<number, LiquidityBucket>();
    const askBuckets = new Map<number, LiquidityBucket>();
    for (const bid of orderBookSnapshot.bids) {
      if (bid.price < minPrice || bid.price >= mid) continue;
      const idx = Math.floor((bid.price - minPrice) / bucketSize);
      const bucketMid = minPrice + (idx + 0.5) * bucketSize;
      const existing = bidBuckets.get(idx);
      if (existing) existing.notional += bid.notional;else bidBuckets.set(idx, {
        price: bucketMid,
        notional: bid.notional,
        side: "bid"
      });
    }
    for (const ask of orderBookSnapshot.asks) {
      if (ask.price > maxPrice || ask.price <= mid) continue;
      const idx_0 = Math.floor((ask.price - minPrice) / bucketSize);
      const bucketMid_0 = minPrice + (idx_0 + 0.5) * bucketSize;
      const existing_0 = askBuckets.get(idx_0);
      if (existing_0) existing_0.notional += ask.notional;else askBuckets.set(idx_0, {
        price: bucketMid_0,
        notional: ask.notional,
        side: "ask"
      });
    }

    // Take the top N strongest walls per side
    const TOP_N = 8;
    const topBids = Array.from(bidBuckets.values()).sort((a_3, b_3) => b_3.notional - a_3.notional).slice(0, TOP_N);
    const topAsks = Array.from(askBuckets.values()).sort((a_4, b_4) => b_4.notional - a_4.notional).slice(0, TOP_N);
    const allWalls = [...topBids, ...topAsks];
    if (allWalls.length === 0) return;
    const maxWallNotional = Math.max(...allWalls.map(w => w.notional));
    const formatNotional = (n: number): string => {
      if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
      return `$${n.toFixed(0)}`;
    };
    for (const wall of allWalls) {
      const intensity = wall.notional / maxWallNotional;
      // Line width 1-4 based on intensity
      const lineWidth = Math.max(1, Math.min(4, Math.round(intensity * 4))) as 1 | 2 | 3 | 4;
      const alpha = Math.max(0.3, Math.min(0.9, intensity));
      const color_0 = wall.side === "bid" ? `rgba(34, 197, 94, ${alpha})` : `rgba(239, 68, 68, ${alpha})`;
      const priceLine = mainSeries.createPriceLine({
        price: wall.price,
        color: color_0,
        lineWidth,
        lineStyle: 0,
        // solid
        axisLabelVisible: true,
        title: formatNotional(wall.notional)
      });
      liquidityPriceLinesRef.current.push({
        series: mainSeries,
        line: priceLine
      });
    }
  }, [orderBookSnapshot, indicators.liquidity, dataMode, tradingPair, chartType, klines]);

  // Update current (live) candle
  useEffect(() => {
    if (!currentKline || !isBinanceAvailable || dataMode === "marketcap") return;
    const pp = livePairPrice ?? snappedPairPrice;
    const conv = (p_14: number) => {
      if (tradingPair !== "USDT" && pp && pp > 0) return p_14 / pp;
      return p_14;
    };
    if (chartType === "candle" && candleSeriesRef.current) {
      candleSeriesRef.current.update({
        time: currentKline.time as Time,
        open: conv(currentKline.open),
        high: conv(currentKline.high),
        low: conv(currentKline.low),
        close: conv(currentKline.close)
      });
    } else if (chartType === "line" && lineSeriesRef.current) {
      lineSeriesRef.current.update({
        time: currentKline.time as Time,
        value: conv(currentKline.close)
      });
    }
    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.update({
        time: currentKline.time as Time,
        value: currentKline.volume,
        color: currentKline.close >= currentKline.open ? CHART_COLORS.volumeUp : CHART_COLORS.volumeDown
      });
    }
  }, [currentKline, chartType, dataMode, tradingPair, livePairPrice, snappedPairPrice, isBinanceAvailable]);
  return <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,7%)] overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-400">
                {tokenName}
              </span>
              <span className="text-xs text-gray-600 uppercase">
                {dataMode === "marketcap" ? `${symbol} MCap` : `${symbol}/${tradingPair}`}
              </span>
              {isBinanceAvailable && <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-emerald-400" : "bg-yellow-400"}`} title={isConnected ? "Live" : "Reconnecting..."} />}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-2xl font-bold text-white font-mono">
                {displayPrice != null ? dataMode === "marketcap" ? formatMarketCap(displayPrice) : tradingPair === "USDT" ? `$${formatPrice(displayPrice)}` : `${formatPairPrice(displayPrice)} ${tradingPair}` : "--"}
              </span>
              {priceChangePercent != null && !isNaN(priceChangePercent) && <span className={`text-sm font-semibold ${priceChangePercent >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {priceChangePercent >= 0 ? "+" : ""}
                  {priceChangePercent.toFixed(2)}%
                </span>}
              {hoveredTime && <span className="text-xs text-gray-500 ml-2">{hoveredTime}</span>}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Data mode toggle */}
          <div className="flex rounded-lg overflow-hidden border border-[hsl(230,15%,20%)]">
            <button onClick={() => setDataMode("price")} className={`px-3 py-1.5 text-xs font-medium transition-colors ${dataMode === "price" ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
              Price
            </button>
            <button onClick={() => setDataMode("marketcap")} className={`px-3 py-1.5 text-xs font-medium transition-colors ${dataMode === "marketcap" ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
              Market Cap
            </button>
          </div>

          {/* Trading pair toggle */}
          {dataMode === "price" && <div className="flex rounded-lg overflow-hidden border border-[hsl(230,15%,20%)]">
              {(["USDT", "BTC", "ETH"] as TradingPair[]).map(pair => <button key={pair} onClick={() => setTradingPair(pair)} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${tradingPair === pair ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
                  {pair === "USDT" ? "USD" : pair}
                </button>)}
            </div>}

          {/* Chart type toggle */}
          {dataMode === "price" && <div className="flex rounded-lg overflow-hidden border border-[hsl(230,15%,20%)]">
              <button onClick={() => setChartType("candle")} className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartType === "candle" ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
                Candles
              </button>
              <button onClick={() => setChartType("line")} className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartType === "line" ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
                Line
              </button>
            </div>}

          {/* Indicators menu */}
          {isBinanceAvailable && dataMode === "price" && <div className="relative">
              <button onClick={() => setShowIndicatorMenu(v => !v)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${Object.values(indicators).some(Boolean) ? "bg-blue-600 border-blue-500 text-white" : "bg-[hsl(230,15%,12%)] border-[hsl(230,15%,20%)] text-gray-400 hover:text-white"}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Indicators
                {Object.values(indicators).filter(Boolean).length > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-white/20 text-[10px]">
                    {Object.values(indicators).filter(Boolean).length}
                  </span>}
              </button>
              {showIndicatorMenu && <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowIndicatorMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-[hsl(230,15%,20%)] bg-[hsl(230,15%,10%)] shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2 border-b border-[hsl(230,15%,20%)] text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Overlays
                    </div>
                    {([{
                key: "ma20",
                label: "SMA 20",
                color: "#f59e0b"
              }, {
                key: "ma50",
                label: "SMA 50",
                color: "#3b82f6"
              }, {
                key: "ma200",
                label: "SMA 200",
                color: "#ec4899"
              }, {
                key: "ema21",
                label: "EMA 21",
                color: "#a855f7"
              }, {
                key: "bbands",
                label: "Bollinger Bands",
                color: "#8b5cf6"
              }, {
                key: "liquidity",
                label: "Liquidity Walls",
                color: "#22c55e"
              }, {
                key: "forecast",
                label: "Price Forecast",
                color: "#eab308"
              }] as const).map(opt => <button key={opt.key} onClick={() => toggleIndicator(opt.key)} className="flex items-center justify-between w-full px-3 py-2 text-xs text-left hover:bg-[hsl(230,15%,14%)] transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{
                    backgroundColor: opt.color
                  }} />
                          <span className="text-gray-300">{opt.label}</span>
                        </div>
                        <span className={`w-4 h-4 rounded border ${indicators[opt.key] ? "bg-blue-600 border-blue-500" : "border-gray-600"} flex items-center justify-center`}>
                          {indicators[opt.key] && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>}
                        </span>
                      </button>)}
                    <div className="px-3 py-2 border-y border-[hsl(230,15%,20%)] text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Oscillators (separate pane)
                    </div>
                    {([{
                key: "rsi",
                label: "RSI 14",
                color: "#06b6d4"
              }, {
                key: "macd",
                label: "MACD",
                color: "#3b82f6"
              }] as const).map(opt_0 => <button key={opt_0.key} onClick={() => toggleIndicator(opt_0.key)} className="flex items-center justify-between w-full px-3 py-2 text-xs text-left hover:bg-[hsl(230,15%,14%)] transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{
                    backgroundColor: opt_0.color
                  }} />
                          <span className="text-gray-300">{opt_0.label}</span>
                        </div>
                        <span className={`w-4 h-4 rounded border ${indicators[opt_0.key] ? "bg-blue-600 border-blue-500" : "border-gray-600"} flex items-center justify-center`}>
                          {indicators[opt_0.key] && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>}
                        </span>
                      </button>)}
                    {Object.values(indicators).some(Boolean) && <button onClick={() => setIndicators(DEFAULT_INDICATORS)} className="w-full px-3 py-2 text-xs text-red-400 hover:bg-[hsl(230,15%,14%)] transition-colors border-t border-[hsl(230,15%,20%)]">
                        Clear all
                      </button>}
                  </div>
                </>}
            </div>}

          {/* Interval selector */}
          {isBinanceAvailable && dataMode === "price" && <div className="flex rounded-lg overflow-hidden border border-[hsl(230,15%,20%)]">
              {INTERVAL_OPTIONS.map(opt_1 => <button key={opt_1.value} onClick={() => setInterval(opt_1.value)} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${interval === opt_1.value ? "bg-blue-600 text-white" : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"}`}>
                  {opt_1.label}
                </button>)}
            </div>}
        </div>
      </div>

      {/* Ticker stats bar */}
      {ticker && <div className="flex items-center gap-6 px-4 py-2 border-b border-[hsl(var(--border))] text-xs overflow-x-auto">
          <div>
            <span className="text-gray-500 mr-1">24h High</span>
            <span className="text-white font-mono">${formatPrice(ticker.high24h)}</span>
          </div>
          <div>
            <span className="text-gray-500 mr-1">24h Low</span>
            <span className="text-white font-mono">${formatPrice(ticker.low24h)}</span>
          </div>
          <div>
            <span className="text-gray-500 mr-1">24h Vol</span>
            <span className="text-white font-mono">{formatVolume(ticker.volume24h)} {symbol}</span>
          </div>
          <div>
            <span className="text-gray-500 mr-1">24h Vol (USDT)</span>
            <span className="text-white font-mono">${formatVolume(ticker.quoteVolume24h)}</span>
          </div>
        </div>}

      {/* Forecast summary badge */}
      {indicators.forecast && forecastSummary && <div className="flex items-center gap-3 px-4 py-2 border-b border-[hsl(var(--border))] bg-[hsl(230,15%,6%)] text-xs overflow-x-auto">
          <span className="text-gray-500 uppercase tracking-wider text-[10px]">
            Forecast
          </span>
          <div className="flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${forecastSummary.trend === "bullish" ? "bg-emerald-400" : forecastSummary.trend === "bearish" ? "bg-red-400" : "bg-yellow-400"}`} />
            <span className={`font-semibold capitalize ${forecastSummary.trend === "bullish" ? "text-emerald-400" : forecastSummary.trend === "bearish" ? "text-red-400" : "text-yellow-400"}`}>
              {forecastSummary.trend}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Target:</span>
            <span className="font-mono font-semibold text-white">
              ${formatPrice(forecastSummary.targetPrice)}
            </span>
            <span className={`font-mono ${forecastSummary.targetChangePct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              ({forecastSummary.targetChangePct >= 0 ? "+" : ""}
              {forecastSummary.targetChangePct.toFixed(2)}%)
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Confidence:</span>
            <span className="font-mono font-semibold text-white">
              {forecastSummary.confidencePct.toFixed(0)}%
            </span>
          </div>
          <span className="text-[10px] text-gray-600 ml-auto italic">
            Statistical projection · not financial advice
          </span>
        </div>}

      {/* Chart — height grows when oscillator panes are active */}
      <div ref={chartContainerRef} className="w-full" style={{
      height: 500 + (indicators.rsi ? 140 : 0) + (indicators.macd ? 140 : 0)
    }} />

      {/* Footer info */}
      {!isBinanceAvailable && <div className="px-4 py-2 border-t border-[hsl(var(--border))] text-xs text-gray-500">
          Real-time data unavailable for this token. Showing historical price data.
        </div>}
    </div>;
}